import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { applyTenantText, parseHelpPage, selectPages, type HelpPage } from '@teslyn/help';
import { Button } from '../components/Button.tsx';
import { useMediaQuery } from '../components/useMediaQuery.ts';

// Help pages are bundled (ADR-012); the portal shows the `cloud` product pages the viewer's role may see.
const RAW = import.meta.glob('../../../../content/help/cloud/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

const PAGES: HelpPage[] = Object.entries(RAW).map(([path, text]) => parseHelpPage(text, path));

/**
 * Below 900 px the panel is a full-screen sheet (a modal dialog); from 900 px up it docks to the right edge beside
 * the page (complementary). Keep in step with the media queries in styles.css.
 */
export const HELP_SHEET_QUERY = '(max-width: 899.98px)';

/** True while the viewport is narrow enough for the full-screen sheet. */
function useSheet(): boolean {
  return useMediaQuery(HELP_SHEET_QUERY);
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

export interface HelpPanelProps {
  readonly pageId: string;
  /**
   * Roles for page filtering (a page shows when any of them may see it); `null` before sign-in (only pages open to
   * every role or listing `anonymous`).
   */
  readonly roles: readonly string[] | null;
  readonly tenantName: string;
  readonly productTitle: string;
  readonly onClose: () => void;
}

/**
 * Contextual help (ADR-012). Docked to the right edge on desktop with its own scroll; a full-screen sheet on narrow
 * screens. Focus moves to the panel title when it opens and back to the control that opened it when it closes;
 * Escape closes it.
 */
export function HelpPanel({
  pageId,
  roles,
  tenantName,
  productTitle,
  onClose,
}: HelpPanelProps): ReactElement {
  const roleKey = (roles ?? ['anonymous']).join(',');
  const page = useMemo(() => {
    for (const role of roleKey.split(',')) {
      const visible = selectPages(PAGES, { product: 'cloud', role, features: new Set() });
      const found = visible.find((p) => p.id === pageId);
      if (found) return found;
    }
    return null;
  }, [pageId, roleKey]);
  const body = page ? applyTenantText(page.body, { name: tenantName, productTitle }) : null;
  const sheet = useSheet();
  const panelRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  // The control that opened the panel (focused while this first renders), to give focus back on close.
  const [opener] = useState<HTMLElement | null>(() =>
    document.activeElement instanceof HTMLElement && document.activeElement !== document.body
      ? document.activeElement
      : null,
  );

  useEffect(() => {
    titleRef.current?.focus();
    return () => {
      if (opener?.isConnected) opener.focus();
    };
  }, [opener]);

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      // The sheet is modal: keep Tab inside it.
      if (e.key !== 'Tab' || !sheet || !panelRef.current) return;
      const items = [...panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)];
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) return;
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === titleRef.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [sheet, onClose]);

  return (
    <aside
      ref={panelRef}
      className={sheet ? 'help help--sheet' : 'help'}
      aria-label="Help"
      {...(sheet ? { role: 'dialog', 'aria-modal': true } : {})}
    >
      <div className="help__bar">
        <h2 ref={titleRef} className="heading help__title" tabIndex={-1}>
          {page ? page.title : 'Help'}
        </h2>
        <Button variant="quiet" onClick={onClose} aria-label="Close help">
          Close
        </Button>
      </div>
      <div className="help__body body">
        <div className="help__text">
          {body ? (
            <Markdown
              remarkPlugins={[remarkGfm]}
              components={{
                // The panel title is the h2; the page's own sections sit one level below it.
                h1: ({ children }) => <h3 className="heading">{children}</h3>,
                h2: ({ children }) => <h3 className="heading">{children}</h3>,
                h3: ({ children }) => <h4 className="body help__sub">{children}</h4>,
                // Screenshot placeholders are filled by the guide build; react-markdown blanks the unknown
                // `screenshot:` scheme, so an empty src is a placeholder too.
                img: ({ src, alt }) =>
                  typeof src !== 'string' || src === '' || src.startsWith('screenshot:') ? null : (
                    <img src={src} alt={alt ?? ''} />
                  ),
              }}
            >
              {body}
            </Markdown>
          ) : (
            <p className="muted">No help has been written for this screen yet.</p>
          )}
        </div>
      </div>
    </aside>
  );
}
